import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAudio } from '../contexts/AudioContext';
import { VideoPlayer } from '../components/VideoPlayer';
import { AudioPlayer } from '../components/AudioPlayer';
import './ContentPlayer.css';

interface SlideData {
  index: number;
  image: string;
  audio: string | null;
  duration: number;
  transition?: string;
}

interface SyncData {
  id: string;
  title: string;
  title_fa?: string;
  slides: SlideData[];
  settings?: {
    autoAdvance?: boolean;
    loopPresentation?: boolean;
    defaultTransition?: string;
    transitionDuration?: number;
  };
}

interface ContentData {
  id: string;
  title: string;
  title_fa?: string;
  description?: string;
  description_fa?: string;
  content_type?: string;        // 'video', 'audio', 'pdf', 'slides'
  r2_path?: string;             // Direct path for curriculum content
  r2_audio_path?: string;       // Legacy: audio folder path
  r2_slides_path?: string;      // Legacy: slides folder path
  sync_data?: SyncData;         // Legacy: slide sync data
  duration_seconds?: number;
  slide_count?: number;
  file_size_bytes?: number;
  grade?: number;
  course?: string;
  topic_number?: number;
  school_name?: string;
  school_name_fa?: string;
}

// R2 CDN base URL
const R2_CDN_URL = 'https://cdn.solarmaktab.com';

export function ContentPlayer() {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, isLoading: authLoading } = useAuth();
  const { playTrack } = useAudio();
  
  const [content, setContent] = useState<ContentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Legacy player state (for slides+audio)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [slideDuration, setSlideDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerStartRef = useRef<number>(0);

  const isRTL = i18n.language === 'fa';

  // Determine content type
  const getContentType = useCallback((): 'video' | 'audio' | 'pdf' | 'slides' | 'unknown' => {
    if (!content) return 'unknown';
    
    // Check explicit content_type field first
    if (content.content_type) {
      return content.content_type as 'video' | 'audio' | 'pdf' | 'slides';
    }
    
    // Check r2_path extension for curriculum content
    if (content.r2_path) {
      const ext = content.r2_path.split('.').pop()?.toLowerCase();
      if (ext === 'mp4' || ext === 'webm' || ext === 'mov') return 'video';
      if (ext === 'm4a' || ext === 'mp3' || ext === 'wav') return 'audio';
      if (ext === 'pdf') return 'pdf';
    }
    
    // Legacy content detection
    if (content.sync_data && content.r2_slides_path) {
      return 'slides';
    }
    
    return 'unknown';
  }, [content]);

  // Check if current slide has audio (legacy)
  const currentSlideHasAudio = useCallback((): boolean => {
    if (!content?.sync_data?.slides || !content.r2_audio_path) return false;
    const slide = content.sync_data.slides[currentSlideIndex];
    return !!(slide?.audio);
  }, [content, currentSlideIndex]);

  // Check if content has any audio at all (legacy)
  const contentHasAudio = useCallback((): boolean => {
    if (!content?.sync_data?.slides || !content.r2_audio_path) return false;
    return content.sync_data.slides.some(slide => slide.audio);
  }, [content]);

  // Fetch content data
  useEffect(() => {
    const fetchContent = async () => {
      if (!accessToken || !id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/student/content/${id}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || 'Failed to fetch content');
        }

        setContent(data.data?.content);
      } catch (err) {
        console.error('Error fetching content:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch content');
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      fetchContent();
    }
  }, [accessToken, id, authLoading]);

  // Get slide image URL (legacy)
  const getSlideImageUrl = useCallback((slide: SlideData): string => {
    if (!content?.r2_slides_path) return '';
    const contentBasePath = content.r2_slides_path.replace(/slides\/$/, '');
    return `${R2_CDN_URL}/${contentBasePath}${slide.image}`;
  }, [content]);

  // Get audio URL for a specific slide (legacy)
  const getAudioUrlForSlide = useCallback((slideIndex: number): string => {
    if (!content?.sync_data?.slides || !content.r2_audio_path) return '';
    const slide = content.sync_data.slides[slideIndex];
    if (!slide?.audio) return '';
    const contentBasePath = content.r2_audio_path.replace(/audio\/$/, '');
    return `${R2_CDN_URL}/${contentBasePath}${slide.audio}`;
  }, [content]);

  // Get slide duration in seconds (legacy)
  const getSlideDuration = useCallback((slideIndex: number): number => {
    if (!content?.sync_data?.slides) return 5;
    const slide = content.sync_data.slides[slideIndex];
    return slide ? (slide.duration / 1000) : 5;
  }, [content]);

  // Clear timer (legacy)
  const clearSlideTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start timer for slides without audio (legacy)
  const startSlideTimer = useCallback(() => {
    clearSlideTimer();
    const duration = getSlideDuration(currentSlideIndex);
    setSlideDuration(duration);
    timerStartRef.current = Date.now();
    setCurrentTime(0);

    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - timerStartRef.current) / 1000;
      setCurrentTime(elapsed);

      if (elapsed >= duration) {
        clearSlideTimer();
        const slides = content?.sync_data?.slides || [];
        const autoAdvance = content?.sync_data?.settings?.autoAdvance !== false;
        
        if (autoAdvance && currentSlideIndex < slides.length - 1) {
          setCurrentSlideIndex(prev => prev + 1);
        } else if (currentSlideIndex === slides.length - 1) {
          setIsPlaying(false);
          if (content?.sync_data?.settings?.loopPresentation) {
            setCurrentSlideIndex(0);
          }
        }
      }
    }, 100);
  }, [clearSlideTimer, getSlideDuration, currentSlideIndex, content]);

  // Handle audio time update (legacy)
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  // Handle audio loaded (legacy)
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setSlideDuration(audioRef.current.duration);
    }
  }, []);

  // Handle audio ended (legacy)
  const handleEnded = useCallback(() => {
    const slides = content?.sync_data?.slides || [];
    const autoAdvance = content?.sync_data?.settings?.autoAdvance !== false;
    
    if (autoAdvance && currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    } else if (currentSlideIndex === slides.length - 1) {
      setIsPlaying(false);
      if (content?.sync_data?.settings?.loopPresentation) {
        setCurrentSlideIndex(0);
      }
    }
  }, [content, currentSlideIndex]);

  // Load and play audio/timer when slide changes (legacy)
  useEffect(() => {
    if (!content?.sync_data?.slides) return;
    if (getContentType() !== 'slides') return;

    const hasAudio = currentSlideHasAudio();
    
    if (hasAudio && audioRef.current) {
      clearSlideTimer();
      const audioUrl = getAudioUrlForSlide(currentSlideIndex);
      if (audioUrl && audioRef.current.src !== audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        setCurrentTime(0);
        
        if (isPlaying) {
          audioRef.current.play().catch(err => {
            console.error('Error playing audio:', err);
          });
        }
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      
      const duration = getSlideDuration(currentSlideIndex);
      setSlideDuration(duration);
      setCurrentTime(0);
      
      if (isPlaying) {
        startSlideTimer();
      }
    }
  }, [currentSlideIndex, content, getAudioUrlForSlide, isPlaying, currentSlideHasAudio, clearSlideTimer, startSlideTimer, getSlideDuration, getContentType]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearSlideTimer();
    };
  }, [clearSlideTimer]);

  // Play/Pause toggle (legacy)
  const togglePlay = useCallback(() => {
    const hasAudio = currentSlideHasAudio();
    
    if (hasAudio && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(err => {
          console.error('Error playing audio:', err);
        });
      }
    } else {
      if (isPlaying) {
        clearSlideTimer();
        setIsPlaying(false);
      } else {
        setIsPlaying(true);
        startSlideTimer();
      }
    }
  }, [isPlaying, currentSlideHasAudio, clearSlideTimer, startSlideTimer]);

  // Seek to position (legacy)
  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current && slideDuration > 0) {
      const rect = progressRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * slideDuration;
      
      const hasAudio = currentSlideHasAudio();
      
      if (hasAudio && audioRef.current) {
        audioRef.current.currentTime = newTime;
      } else {
        timerStartRef.current = Date.now() - (newTime * 1000);
      }
      setCurrentTime(newTime);
    }
  }, [slideDuration, currentSlideHasAudio]);

  // Go to specific slide (legacy)
  const goToSlide = useCallback((index: number) => {
    if (!content?.sync_data?.slides) return;
    if (index < 0 || index >= content.sync_data.slides.length) return;
    
    clearSlideTimer();
    setCurrentSlideIndex(index);
    setCurrentTime(0);
  }, [content, clearSlideTimer]);

  // Previous slide (legacy)
  const previousSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      goToSlide(currentSlideIndex - 1);
    }
  }, [currentSlideIndex, goToSlide]);

  // Next slide (legacy)
  const nextSlide = useCallback(() => {
    const slides = content?.sync_data?.slides || [];
    if (currentSlideIndex < slides.length - 1) {
      goToSlide(currentSlideIndex + 1);
    }
  }, [content, currentSlideIndex, goToSlide]);

  // Toggle mute (legacy)
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Handle volume change (legacy)
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  // Format time
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate total duration (legacy)
  const getTotalDuration = useCallback((): number => {
    if (!content?.sync_data?.slides) return 0;
    return content.sync_data.slides.reduce((sum, slide) => sum + (slide.duration / 1000), 0);
  }, [content]);

  // Calculate overall progress (legacy)
  const getOverallProgress = useCallback((): number => {
    if (!content?.sync_data?.slides) return 0;
    let elapsed = 0;
    for (let i = 0; i < currentSlideIndex; i++) {
      elapsed += content.sync_data.slides[i].duration / 1000;
    }
    elapsed += currentTime;
    const total = getTotalDuration();
    return total > 0 ? (elapsed / total) * 100 : 0;
  }, [content, currentSlideIndex, currentTime, getTotalDuration]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle audio play with persistent player
  const handleAudioPlay = useCallback(() => {
    if (content?.r2_path) {
      playTrack({
        id: content.id,
        src: content.r2_path,
        title: content.title,
        titleFa: content.title_fa,
        duration: content.duration_seconds,
        contentType: content.content_type,
        grade: content.grade,
        course: content.course,
        topicNumber: content.topic_number
      });
    }
  }, [content, playTrack]);

  if (authLoading || isLoading) {
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cp-loading">
          {isRTL ? 'در حال بارگذاری...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cp-error">
          <h2>{isRTL ? 'خطا' : 'Error'}</h2>
          <p>{error}</p>
          <button className="cp-btn primary" onClick={handleBack}>
            {isRTL ? 'بازگشت' : 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cp-error">
          <h2>{isRTL ? 'محتوا یافت نشد' : 'Content Not Found'}</h2>
          <button className="cp-btn primary" onClick={handleBack}>
            {isRTL ? 'بازگشت' : 'Go Back'}
          </button>
        </div>
      </div>
    );
  }

  const displayTitle = isRTL && content.title_fa ? content.title_fa : content.title;
  const contentType = getContentType();

  // Render Video Player
  if (contentType === 'video' && content.r2_path) {
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <header className="cp-header">
          <button className="cp-back-btn" onClick={handleBack}>
            ← {isRTL ? 'بازگشت' : 'Back'}
          </button>
          <h1 className="cp-title">{displayTitle}</h1>
          <div className="cp-content-type-badge">🎬 {isRTL ? 'ویدیو' : 'Video'}</div>
        </header>
        
        <div className="cp-video-container">
          <VideoPlayer
            src={content.r2_path}
            title={content.title}
            titleFa={content.title_fa}
            onEnded={() => console.log('Video ended')}
            onError={(err) => console.error('Video error:', err)}
          />
        </div>
        
        {(content.description || content.description_fa) && (
          <div className="cp-description">
            <h3>{isRTL ? 'توضیحات' : 'Description'}</h3>
            <p>{isRTL ? (content.description_fa || content.description) : content.description}</p>
          </div>
        )}
      </div>
    );
  }

  // Render Audio Player
  if (contentType === 'audio' && content.r2_path) {
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <header className="cp-header">
          <button className="cp-back-btn" onClick={handleBack}>
            ← {isRTL ? 'بازگشت' : 'Back'}
          </button>
          <h1 className="cp-title">{displayTitle}</h1>
          <div className="cp-content-type-badge">🎧 {isRTL ? 'صوت' : 'Audio'}</div>
        </header>
        
        <div className="cp-audio-container">
          <div className="cp-audio-artwork">
            <div className="cp-audio-icon">🎧</div>
            <h2 className="cp-audio-title">{displayTitle}</h2>
            {content.school_name && (
              <p className="cp-audio-school">
                {isRTL ? (content.school_name_fa || content.school_name) : content.school_name}
              </p>
            )}
          </div>
          
          <AudioPlayer
            src={content.r2_path}
            title={content.title}
            titleFa={content.title_fa}
            duration={content.duration_seconds}
            onEnded={() => console.log('Audio ended')}
            onError={(err) => console.error('Audio error:', err)}
          />
          
          {/* Option to use persistent mini player */}
          <div className="cp-audio-actions">
            <button className="cp-btn secondary" onClick={handleAudioPlay}>
              🎵 {isRTL ? 'پخش در پس‌زمینه' : 'Play in Background'}
            </button>
          </div>
        </div>
        
        {(content.description || content.description_fa) && (
          <div className="cp-description">
            <h3>{isRTL ? 'توضیحات' : 'Description'}</h3>
            <p>{isRTL ? (content.description_fa || content.description) : content.description}</p>
          </div>
        )}
      </div>
    );
  }

  // Render PDF Viewer
  if (contentType === 'pdf' && content.r2_path) {
    const pdfUrl = `${R2_CDN_URL}/${content.r2_path}`;
    // Use Google Docs viewer as a reliable cross-browser PDF viewer
    const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
    
    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        <header className="cp-header">
          <button className="cp-back-btn" onClick={handleBack}>
            ← {isRTL ? 'بازگشت' : 'Back'}
          </button>
          <h1 className="cp-title">{displayTitle}</h1>
          <div className="cp-content-type-badge">📄 {isRTL ? 'PDF' : 'PDF'}</div>
        </header>
        
        <div className="cp-pdf-container">
          <div className="cp-pdf-toolbar">
            <a 
              href={pdfUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="cp-btn secondary"
            >
              🔗 {isRTL ? 'باز کردن در تب جدید' : 'Open in New Tab'}
            </a>
            <a 
              href={pdfUrl} 
              download
              className="cp-btn secondary"
            >
              📥 {isRTL ? 'دانلود' : 'Download'}
            </a>
          </div>
          
          <iframe
            src={googleDocsViewerUrl}
            className="cp-pdf-viewer"
            title={displayTitle}
            allowFullScreen
          />
        </div>
        
        {(content.description || content.description_fa) && (
          <div className="cp-description">
            <h3>{isRTL ? 'توضیحات' : 'Description'}</h3>
            <p>{isRTL ? (content.description_fa || content.description) : content.description}</p>
          </div>
        )}
      </div>
    );
  }

  // Render Legacy Slides+Audio Player
  if (contentType === 'slides' && content.sync_data?.slides) {
    const slides = content.sync_data.slides;
    const currentSlide = slides[currentSlideIndex];
    const progress = slideDuration > 0 ? (currentTime / slideDuration) * 100 : 0;
    const hasAudio = contentHasAudio();
    const slideHasAudio = currentSlideHasAudio();

    return (
      <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
        {/* Header */}
        <header className="cp-header">
          <button className="cp-back-btn" onClick={handleBack}>
            ← {isRTL ? 'بازگشت' : 'Back'}
          </button>
          <h1 className="cp-title">{displayTitle}</h1>
          <div className="cp-slide-counter">
            {currentSlideIndex + 1} / {slides.length}
            {!slideHasAudio && slides.length > 0 && (
              <span className="cp-no-audio-badge" title={isRTL ? 'بدون صدا' : 'No audio'}>
                🔇
              </span>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="cp-content">
          {/* Slide Display */}
          <div className="cp-slide-container">
            {currentSlide ? (
              <img 
                src={getSlideImageUrl(currentSlide)} 
                alt={`Slide ${currentSlideIndex + 1}`}
                className="cp-slide-image"
              />
            ) : (
              <div className="cp-no-slide">
                <span className="cp-no-slide-icon">📄</span>
                <p>{isRTL ? 'اسلایدی موجود نیست' : 'No slide available'}</p>
              </div>
            )}
          </div>

          {/* Slide Thumbnails */}
          {slides.length > 1 && (
            <div className="cp-thumbnails">
              {slides.map((slide, index) => (
                <button
                  key={index}
                  className={`cp-thumbnail ${index === currentSlideIndex ? 'active' : ''} ${!slide.audio ? 'no-audio' : ''}`}
                  onClick={() => goToSlide(index)}
                >
                  <img 
                    src={getSlideImageUrl(slide)} 
                    alt={`Thumbnail ${index + 1}`}
                  />
                  <span className="cp-thumbnail-number">{index + 1}</span>
                  {!slide.audio && <span className="cp-thumbnail-no-audio">🔇</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Player Controls */}
        <div className="cp-controls">
          {/* Overall Progress Bar */}
          <div className="cp-overall-progress">
            <div 
              className="cp-overall-progress-fill" 
              style={{ width: `${getOverallProgress()}%` }}
            />
          </div>

          {/* Current Slide Progress Bar */}
          <div 
            className="cp-progress-container" 
            ref={progressRef}
            onClick={handleSeek}
          >
            <div className="cp-progress-bar">
              <div 
                className="cp-progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Time Display */}
          <div className="cp-time">
            <span>{formatTime(currentTime)}</span>
            <span>/</span>
            <span>{formatTime(slideDuration)}</span>
            <span className="cp-total-time">
              ({formatTime(getTotalDuration())} total)
            </span>
          </div>

          {/* Control Buttons */}
          <div className="cp-buttons">
            <button 
              className="cp-control-btn" 
              onClick={previousSlide}
              disabled={currentSlideIndex === 0}
            >
              ⏮️
            </button>
            
            <button className="cp-play-btn" onClick={togglePlay}>
              {isPlaying ? '⏸️' : '▶️'}
            </button>
            
            <button 
              className="cp-control-btn" 
              onClick={nextSlide}
              disabled={currentSlideIndex === slides.length - 1}
            >
              ⏭️
            </button>

            {/* Volume controls */}
            {hasAudio && (
              <div className="cp-volume">
                <button className="cp-control-btn" onClick={toggleMute}>
                  {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="cp-volume-slider"
                />
              </div>
            )}
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          preload="auto"
        />
      </div>
    );
  }

  // Unknown content type
  return (
    <div className={`content-player ${isRTL ? 'rtl' : 'ltr'}`}>
      <header className="cp-header">
        <button className="cp-back-btn" onClick={handleBack}>
          ← {isRTL ? 'بازگشت' : 'Back'}
        </button>
        <h1 className="cp-title">{displayTitle}</h1>
      </header>
      
      <div className="cp-error">
        <h2>{isRTL ? 'نوع محتوا پشتیبانی نمی‌شود' : 'Unsupported Content Type'}</h2>
        <p>{isRTL ? 'این نوع محتوا هنوز پشتیبانی نمی‌شود.' : 'This content type is not yet supported.'}</p>
        <p className="cp-debug-info">
          Content Type: {contentType || 'unknown'}<br/>
          R2 Path: {content.r2_path || 'none'}<br/>
          Has Sync Data: {content.sync_data ? 'yes' : 'no'}
        </p>
        <button className="cp-btn primary" onClick={handleBack}>
          {isRTL ? 'بازگشت' : 'Go Back'}
        </button>
      </div>
    </div>
  );
}
